import React, { Fragment, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import { Transition } from 'react-transition-group';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useDispatch } from 'react-redux';

import { getAvailableProductsCount } from '@thunks/products';

import {
  decodeQueryFilters,
  encodeSelectedFilters,
  toggleItem,
} from '@utils/filters';

import {
  ColorsFiltersSection,
  SizeFiltersSection,
  SortFiltersSection,
} from '@common/components/Filters/FiltersSections';
import { OFFER_TYPES, ORDER_NEWEST } from '@common/constants/filters';
import { FiltersToggleButton } from '../FiltersToggleButton';

import {
  ButtonsRow,
  CategoriesContainer,
  Counter,
  FiltersModal,
  MobileWrapper,
  MobileWrapperInner,
  ResetButton,
  ResetButtonContainer,
  StyledCloseButton,
  SubmitButton,
  Title,
  TitleContainer,
} from './Filters.styled';
import { FiltersContext } from './FiltersContext';

function Filters({
  isFetching,
  colorOptions,
  sizeOptions,
  category,
  offerType,
  newArrivalOnly,
  publishedOnly,
  fetchFiltersOptions,
  shouldRenderExternalToggleButton,
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    color: queryColor,
    size: querySize,
    ordering: queryOrdering,
  } = router.query;

  const {
    selectedOrdering,
    selectedColors,
    selectedSizes,
    availableProductsCount,
    isFiltered,
    allFiltersCount,
    isFiltersModalOpen,
    setAvailableProductsCount,
    setColors,
    setIsFiltered,
    setOrdering,
    setSizes,
    openFiltersModal,
    closeFiltersModal,
  } = useContext(FiltersContext);

  const [visibleMobileSection, setVisibleMobileSection] = useState(null);
  const [isSectionOpened, setSectionOpened] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const handleOrderingSelected = value => {
    setOrdering(value);
  };

  const handleColorsChange = color => {
    const newColors = toggleItem(selectedColors, color);

    setColors(newColors);

    const [, filtersOptionsObject] = encodeSelectedFilters({
      category,
      color: newColors,
      newArrivalOnly,
      offerType,
      publishedOnly,
    });

    fetchFiltersOptions(filtersOptionsObject, {
      setPartial: 'sizes',
    });
  };

  const handleSizesChange = size => {
    const newSizes = toggleItem(selectedSizes, size);

    setSizes(newSizes);

    const [, filtersOptionsObject] = encodeSelectedFilters({
      category,
      newArrivalOnly,
      offerType,
      publishedOnly,
      size: newSizes,
    });

    fetchFiltersOptions(filtersOptionsObject, {
      setPartial: 'colors',
    });
  };

  const openSection = sectionName => {
    setVisibleMobileSection(sectionName);
  };

  const closeSection = () => {
    setSectionOpened(false);
  };

  const applyFilters = () => {
    const {
      asPath,
      pathname,
      query: { page, ...query },
    } = router;
    const [parametrizedPathname] = asPath.split('?');

    const filters = {
      color: selectedColors,
      ordering: selectedOrdering,
      size: selectedSizes,
    };

    const [stringifiedFilters, filtersObject] = encodeSelectedFilters(filters);
    const cleanedQuery = pickBy(
      {
        ...query,
        ...filtersObject,
      },
      identity,
    );

    router.push(
      {
        pathname,
        query: cleanedQuery,
      },
      `/${router.locale}${parametrizedPathname}${stringifiedFilters}`,
    );
    closeFiltersModal();
  };

  const resetFilters = () => {
    setOrdering(ORDER_NEWEST);
    setColors([]);
    setSizes([]);
  };

  useEffect(() => {
    if (visibleMobileSection) {
      setSectionOpened(true);
    }
  }, [visibleMobileSection]);

  useEffect(() => {
    const { colors, ordering, sizes } = decodeQueryFilters({
      color: queryColor,
      ordering: queryOrdering,
      size: querySize,
    });

    setColors(colors);
    setSizes(sizes);
    setOrdering(ordering);

    if (querySize || queryColor) {
      setIsFiltered(true);
      const [, filtersObject] = encodeSelectedFilters({
        category,
        color: selectedColors,
        newArrivalOnly,
        offerType,
        publishedOnly,
        size: selectedSizes,
      });

      fetchFiltersOptions(filtersObject);
    } else {
      setIsFiltered(false);
    }
  }, [queryColor, querySize, queryOrdering]);

  useEffect(() => {
    if (isFiltersModalOpen) {
      const [, filtersOptionsObject] = encodeSelectedFilters({
        category,
        ...(selectedSizes.length > 0 && { color: selectedColors }),
        newArrivalOnly,
        offerType,
        publishedOnly,
      });

      fetchFiltersOptions(filtersOptionsObject, {
        callback: () => setIsInitialized(true),
      });
    }
  }, [isFiltersModalOpen]);

  useEffect(() => {
    const [, productCountFiltersObject] = encodeSelectedFilters({
      category,
      color: selectedColors,
      newArrivalOnly,
      offerType,
      publishedOnly,
      size: selectedSizes,
    });

    dispatch(getAvailableProductsCount(productCountFiltersObject)).then(
      count => {
        setAvailableProductsCount(count);
      },
    );
  }, [selectedColors, selectedSizes, dispatch]);

  return (
    <>
      <Transition
        in={isFiltersModalOpen}
        timeout={{
          enter: 700,
          exit: 700,
        }}
      >
        {modalTransitionState => (
          <FiltersModal
            isOpen={modalTransitionState !== 'exited'}
            transitionState={modalTransitionState}
            onClose={closeFiltersModal}
            withCloseButton={false}
            alwaysMounted
            exit={false}
            enter={false}
            appear={false}
            timeout={0}
            closeButtonId="clothing_filter_close"
            shouldRenderFocusTrap={
              modalTransitionState === 'entered' ||
              modalTransitionState === 'exiting'
            }
          >
            <Transition
              in={isSectionOpened}
              onExited={() => setVisibleMobileSection(null)}
              timeout={700}
            >
              {mobileTransitionState => (
                <MobileWrapper transitionState={mobileTransitionState}>
                  <MobileWrapperInner>
                    <TitleContainer>
                      <Title>{t('filters.mainTitle', 'Filter setzen')}</Title>
                      {allFiltersCount > 0 && (
                        <Counter data-testid="allFiltersCounter">
                          {allFiltersCount}
                        </Counter>
                      )}
                      <StyledCloseButton
                        onClick={closeFiltersModal}
                        buttonId="clothing_filter_close"
                      />
                    </TitleContainer>
                    <CategoriesContainer>
                      <SizeFiltersSection
                        openSection={openSection}
                        closeSection={closeSection}
                        selectedSizes={selectedSizes}
                        visibleMobileSection={visibleMobileSection}
                        allFiltersCount={allFiltersCount}
                        transitionState={mobileTransitionState}
                        isFetching={isFetching && !isInitialized}
                        sizeOptions={sizeOptions}
                        onItemSelect={handleSizesChange}
                      />

                      <ColorsFiltersSection
                        openSection={openSection}
                        closeSection={closeSection}
                        selectedColors={selectedColors}
                        visibleMobileSection={visibleMobileSection}
                        allFiltersCount={allFiltersCount}
                        transitionState={mobileTransitionState}
                        isFetching={isFetching && !isInitialized}
                        colorOptions={colorOptions}
                        onItemSelect={handleColorsChange}
                      />

                      <SortFiltersSection
                        openSection={openSection}
                        closeSection={closeSection}
                        selectedOrdering={selectedOrdering}
                        visibleMobileSection={visibleMobileSection}
                        allFiltersCount={allFiltersCount}
                        transitionState={mobileTransitionState}
                        isFetching={isFetching && !isInitialized}
                        onItemSelect={handleOrderingSelected}
                      />
                    </CategoriesContainer>
                  </MobileWrapperInner>
                </MobileWrapper>
              )}
            </Transition>

            <ButtonsRow transitionState={modalTransitionState}>
              <>
                <SubmitButton
                  onClick={applyFilters}
                  buttonId="clothing_filter_close_show_all_products"
                  disabled={availableProductsCount === 0}
                >
                  {allFiltersCount > 0
                    ? t(
                        'filters.submitFiltersButton.countedProducts',
                        '{{count}} Artikel anzeigen',
                        { count: availableProductsCount },
                      )
                    : t(
                        'filters.submitFiltersButton.allProducts',
                        'Alle Artikel anzeigen',
                      )}
                </SubmitButton>
                <Transition
                  in={allFiltersCount > 0}
                  timeout={{
                    enter: 1,
                    exit: 700,
                  }}
                >
                  {transitionState =>
                    transitionState !== 'exited' && (
                      <ResetButtonContainer transitionState={transitionState}>
                        <ResetButton onClick={resetFilters}>
                          {t('filters.resetFilters', 'Filter löschen')}
                        </ResetButton>
                      </ResetButtonContainer>
                    )
                  }
                </Transition>
              </>
            </ButtonsRow>
          </FiltersModal>
        )}
      </Transition>

      {!shouldRenderExternalToggleButton && (
        <FiltersToggleButton
          onClick={openFiltersModal}
          isFiltered={isFiltered}
          filtersCount={allFiltersCount}
        />
      )}
    </>
  );
}

Filters.defaultProps = {
  category: undefined,
  colorOptions: [],
  isFetching: false,
  newArrivalOnly: false,
  offerType: undefined,
  publishedOnly: true,
  sizeOptions: [],
  shouldRenderExternalToggleButton: false,
};

Filters.propTypes = {
  category: PropTypes.string,
  colorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      colorHex: PropTypes.arrayOf(PropTypes.string),
      count: PropTypes.number,
      id: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  fetchFiltersOptions: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  newArrivalOnly: PropTypes.bool,
  offerType: PropTypes.oneOf(Object.keys(OFFER_TYPES)),
  publishedOnly: PropTypes.bool,
  sizeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      options: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          id: PropTypes.string,
          title: PropTypes.string,
        }),
      ),
    }),
  ),
  shouldRenderExternalToggleButton: PropTypes.bool,
};

export { Filters };
